import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

// CSRF token storage
let csrfToken: string | null = null;

// Function to fetch CSRF token
async function getCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }
  
  try {
    const response = await fetch('/api/auth/csrf-token', {
      method: 'GET',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }
    
    const data = await response.json();
    csrfToken = data.csrfToken;
    if (!csrfToken) {
      throw new Error('No CSRF token received');
    }
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
}

// Refresh CSRF token periodically (every 50 minutes)
setInterval(() => {
  csrfToken = null;
}, 50 * 60 * 1000);

async function throwIfResNotOk(res: Response): Promise<void | { retry: boolean }> {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    
    // Handle CSRF token errors
    if (res.status === 403 && text.includes('CSRF')) {
      // Clear the token and retry once
      csrfToken = null;
      console.warn('CSRF token expired, refreshing...');
      return { retry: true };
    }
    
    // Handle specific calendar reconnect requirements gracefully
    if (text.includes("requiresReconnect") || text.includes("REQUIRES_RECONNECT")) {
      console.warn('🔄 Calendar requires reconnection:', text);
      
      // Parse response to check for reconnect flag
      try {
        const parsed = JSON.parse(text);
        if (parsed.requiresReconnect) {
          console.warn("Calendar session revoked – triggering reconnect prompt");
          alert("Your calendar session has been revoked. Please reconnect your calendar in the settings to continue scheduling interviews.");
          return;
        }
      } catch {
        // If parsing fails, show generic message
        alert("Calendar sync failed. Please reconnect your calendar to continue scheduling interviews.");
      }
    }
    
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  url: string,
  options: {
    method?: string;
    body?: string | FormData;
    credentials?: RequestCredentials;
    headers?: Record<string, string>;
  } = {},
  retryCount = 0
): Promise<Response> {
  const { method = "GET", body, credentials = "include", headers = {} } = options;
  
  // Add CSRF token for state-changing methods
  const needsCsrf = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
  let finalHeaders = { ...headers };
  
  if (needsCsrf) {
    try {
      const token = await getCsrfToken();
      finalHeaders['X-CSRF-Token'] = token;
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
      // Continue without CSRF token (server will reject if required)
    }
  }
  
  const res = await fetch(url, {
    method,
    headers: {
      // Only set Content-Type for string bodies, not FormData (let browser set multipart boundary)
      ...(body && typeof body === 'string' ? { "Content-Type": "application/json" } : {}),
      ...finalHeaders,
    },
    body,
    credentials,
  });

  const result = await throwIfResNotOk(res);
  
  // Retry once if CSRF token expired
  if (result?.retry && retryCount === 0) {
    return apiRequest(url, options, retryCount + 1);
  }
  
  return res;
}

/**
 * API request helper that automatically parses JSON response
 * Use this when you need typed JSON response data
 * Safely handles non-JSON responses (204 No Content, etc.)
 */
export async function apiRequestJson<T>(
  url: string,
  options: {
    method?: string;
    body?: string | FormData;
    credentials?: RequestCredentials;
    headers?: Record<string, string>;
  } = {}
): Promise<T> {
  const response = await apiRequest(url, options);
  
  // Handle 204 No Content and other responses without body
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return {} as T;
  }
  
  // Check if response has JSON content-type
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    console.warn(`apiRequestJson: Expected JSON but got ${contentType} from ${url}`);
    return {} as T;
  }
  
  return await response.json() as T;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    const response = await res.json();
    
    // Extract data from ApiResponse format if present
    if (response && typeof response === 'object' && 'success' in response && response.success && response.data) {
      return response.data;
    }
    
    // Return raw response for non-ApiResponse formats (legacy compatibility)
    return response;
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true, // Refetch when window regains focus for data consistency
      refetchOnMount: true, // Always check for fresh data when component mounts
      refetchOnReconnect: true, // Refetch when network reconnects
      staleTime: 30 * 1000, // 30 seconds - data is fresh for a short time
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Global persister state for user-scoped caching
let currentPersister: ReturnType<typeof createSyncStoragePersister> | null = null;
let currentUserId: string | null = null;

/**
 * Create a user-scoped persister for secure cache isolation
 */
function createUserScopedPersister(userId: string) {
  return createSyncStoragePersister({
    storage: window.localStorage,
    key: `talenture-cache-${userId}`, // USER-SCOPED KEY
  });
}

/**
 * Initialize user-scoped persistence for the current user
 */
export async function initializeUserCache(userId: string) {
  // Skip if same user is already initialized
  if (currentUserId === userId && currentPersister) {
    return;
  }

  console.log('[Security] Initializing user-scoped cache for user:', userId.substring(0, 8) + '...');

  // Clear any existing cache for different user
  if (currentUserId && currentUserId !== userId) {
    await clearUserCache();
  }

  currentUserId = userId;
  currentPersister = createUserScopedPersister(userId);

  // Initialize persistence with RESTRICTED data only
  persistQueryClient({
    queryClient,
    persister: currentPersister,
    maxAge: 30 * 60 * 1000, // REDUCED to 30 minutes for security
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        const queryKey = Array.isArray(query.queryKey) ? query.queryKey.join('') : String(query.queryKey);
        
        // SECURITY: Only persist NON-SENSITIVE job search data
        // Explicitly EXCLUDE user profiles, applications, and personal recommendations
        return (
          queryKey.includes('/api/jobs/search') ||
          queryKey.includes('/api/jobs/public')
        );
      },
    },
  });

  console.log('[Security] User-scoped cache initialized successfully');
}

/**
 * Clear ALL Stytch cookies (Consumer AND B2B) client-side
 * 
 * Following Stytch best practice: when using multiple projects (Consumer + B2B),
 * logout must clear cookies from BOTH projects to prevent auth conflicts.
 * This ensures switching between candidate and company logins works correctly.
 * 
 * @see https://stytch.com/docs/sdks/resources/cookies-and-session-management
 */
export function clearAllStytchCookies(): void {
  console.log('[Security] Clearing all Stytch cookies (Consumer + B2B)');
  
  // All possible Stytch cookie names (Consumer, B2B, and legacy)
  const stytchCookieNames = [
    'stytch_consumer_session',
    'stytch_consumer_jwt',
    'stytch_b2b_session',
    'stytch_b2b_jwt',
    'stytch_session',      // Legacy
    'stytch_session_jwt',  // Legacy
  ];
  
  // Determine cookie domain based on environment
  const hostname = window.location.hostname;
  const isProduction = hostname.includes('talenture.ai');
  const cookieDomain = isProduction ? '.talenture.ai' : undefined;
  
  // Clear each cookie with multiple attribute combinations
  // Browser requires exact attribute match to delete cookies
  for (const cookieName of stytchCookieNames) {
    // Clear with production settings (domain + secure)
    if (isProduction) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}; secure; samesite=none`;
    }
    
    // Clear with development settings (no domain, not secure)
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; samesite=lax`;
    
    // Clear with minimal settings (fallback)
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
  
  console.log('[Security] Stytch cookies cleared');
}

/**
 * Clear all cache data for the current user (logout/switch)
 */
export async function clearUserCache() {
  // Always clear Stytch cookies first (even if no user ID)
  clearAllStytchCookies();
  
  if (!currentUserId) return;

  console.log('[Security] Clearing user cache for user:', currentUserId.substring(0, 8) + '...');

  try {
    // Clear React Query cache
    await queryClient.clear();

    // Clear user-specific localStorage entries
    const userCacheKey = `talenture-cache-${currentUserId}`;
    window.localStorage.removeItem(userCacheKey);
    
    // Clear any legacy global cache (migration safety)
    window.localStorage.removeItem('talenture-cache');
    
    // Clear any other user-scoped entries
    Object.keys(window.localStorage)
      .filter(key => key.startsWith('talenture-cache-'))
      .forEach(key => {
        if (key !== userCacheKey) {
          console.log('[Security] Removing stale cache:', key.substring(0, 32) + '...');
          window.localStorage.removeItem(key);
        }
      });

    // Reset persister state
    currentPersister = null;
    currentUserId = null;

    console.log('[Security] User cache cleared successfully');
  } catch (error) {
    console.error('[Security] Error clearing user cache:', error);
  }
}

/**
 * Clear cache for a different user (admin/security function)
 */
export function clearCacheForUser(userId: string) {
  const userCacheKey = `talenture-cache-${userId}`;
  window.localStorage.removeItem(userCacheKey);
  console.log('[Security] Cleared cache for user:', userId.substring(0, 8) + '...');
}
